import { registerLocaleData } from '@angular/common';
import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import frLocale from '@angular/common/locales/fr';
import nlLocale from '@angular/common/locales/nl';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import { ApplicationConfig, ApplicationRef, EnvironmentProviders, LOCALE_ID, Provider, Type } from '@angular/core';
import { loadTranslations } from '@angular/localize';
import { bootstrapApplication } from '@angular/platform-browser';

import { SOURCE_LANGUAGE, SOURCE_LOCALE, SUPPORTED_LOCALES, SupportedLanguage, toLanguage, toSupportedLocale } from './locales';
import { xliffToJson } from './xliff-to-json';

const LOCALE_DATA: Record<SupportedLanguage, unknown> = {
    de: deLocale,
    en: enLocale,
    fr: frLocale,
    nl: nlLocale,
    pl: plLocale,
    tr: trLocale,
};

/** localStorage key the language switcher writes; see LanguageSwitcherComponent. */
const LOCALE_STORAGE_KEY = 'locale';

export interface BootstrapWithLocaleOptions {
    /**
     * Loads the root component and its app config. Called only *after* translations are in place,
     * because module-level `$localize` calls are evaluated on import and would otherwise stay
     * untranslated.
     */
    load: () => Promise<{ component: Type<unknown>; config: ApplicationConfig }>;

    /** Languages this app ships an XLF for. Defaults to every supported language. */
    languages?: readonly SupportedLanguage[];

    /**
     * Resolve the browser language before the stored one. Off by default: the language switcher
     * writes to storage and reloads, so storage has to win for switching to have any effect.
     */
    preferBrowserLanguage?: boolean;

    /** Runs once the locale is settled, before bootstrap — e.g. to set the dayjs locale. */
    onLocaleResolved?: (language: SupportedLanguage, locale: string) => void;

    /** Providers appended after the app config's own. */
    providers?: (Provider | EnvironmentProviders)[];
}

function readStoredLocale(): string | null {
    try {
        return localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
        // Storage can throw when cookies/site data are blocked.
        return null;
    }
}

function detectLocale(languages: readonly SupportedLanguage[], preferBrowserLanguage: boolean): string {
    const allowed = new Set(languages.map(language => SUPPORTED_LOCALES[language]));
    const candidates = preferBrowserLanguage
        ? [toSupportedLocale(navigator.language), toSupportedLocale(readStoredLocale())]
        : [toSupportedLocale(readStoredLocale()), toSupportedLocale(navigator.language)];

    return candidates.find((locale): locale is string => !!locale && allowed.has(locale)) ?? SOURCE_LOCALE;
}

function applyLanguage(language: SupportedLanguage): void {
    document.documentElement.setAttribute('lang', language);
    registerLocaleData(LOCALE_DATA[language]);
}

/**
 * Resolves the active locale, applies its translations, and returns the locale id that ended up
 * active — which is the source locale when loading the translation file failed.
 */
async function initLocale(languages: readonly SupportedLanguage[], preferBrowserLanguage: boolean): Promise<string> {
    const locale = detectLocale(languages, preferBrowserLanguage);

    if (locale === SOURCE_LOCALE) {
        applyLanguage(SOURCE_LANGUAGE);

        return locale;
    }

    try {
        // Relative to the base href so both dev (/) and prod (/office/) resolve.
        const url = new URL(`assets/locales/messages.${locale}.xlf`, document.baseURI);
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);

        loadTranslations(await xliffToJson(await response.text()));
        $localize.locale = locale;
        applyLanguage(toLanguage(locale));

        return locale;
    } catch (error) {
        console.error(error);
        // Stay on the source language. LOCALE_ID has to match, otherwise the date/number pipes
        // would format against locale data that was never registered.
        applyLanguage(SOURCE_LANGUAGE);

        return SOURCE_LOCALE;
    }
}

/**
 * Loads the translations for the active locale and then bootstraps the application with a matching
 * `LOCALE_ID`.
 *
 * Replaces the hand-rolled bootstrap that used to live in every app's main.ts.
 */
export async function bootstrapWithLocale(options: BootstrapWithLocaleOptions): Promise<ApplicationRef> {
    const languages = options.languages ?? (Object.keys(SUPPORTED_LOCALES) as SupportedLanguage[]);
    const locale = await initLocale(languages, options.preferBrowserLanguage ?? false);

    options.onLocaleResolved?.(toLanguage(locale), locale);

    const { component, config } = await options.load();

    return bootstrapApplication(component, {
        ...config,
        providers: [...config.providers, { provide: LOCALE_ID, useValue: locale }, ...(options.providers ?? [])],
    });
}
